// src/modules/calculators/HeatStress.jsx
import { useState, useMemo } from "react";
import { 
  calculateWBGT, getHeatRiskStatus, getWorkRestCycle, 
  ISO_LIMITS, CLOTHING_ADJUSTMENT 
} from "../../utils/formulas";
// IMPORTACIÓN DEL SERVICIO EXTERNO
import { generateHeatReport } from "../../services/reports/HeatReport";
import { 
  CheckCircle2, Info, ThermometerSun, ShieldAlert, 
  Activity, FileDown, Timer, ArrowRight 
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const HeatStress = ({ cms: cmsRaw = null }) => {
  const riskLabels = cmsRaw?.calor?.riskLabels || null;
  const [formData, setFormData] = useState({
    tgb: "", tg: "", ta: "",
    isOutdoor: false,
    acclimatized: "acclimatized",
    workClass: "class2",
    clothing: "conventional"
  });

  const evaluation = useMemo(() => {
    const { tgb, tg, ta, isOutdoor, acclimatized, workClass, clothing } = formData;
    if (!tgb || !tg) return null;

    const baseWBGT = calculateWBGT(tgb, tg, ta || 0, isOutdoor);
    const cav = CLOTHING_ADJUSTMENT[clothing];
    const effectiveWBGT = baseWBGT + cav;
    const limit = ISO_LIMITS[acclimatized][workClass];
    const status = getHeatRiskStatus(effectiveWBGT, limit, riskLabels);
    const cycle = getWorkRestCycle(effectiveWBGT, limit);

    return { 
      baseWBGT: baseWBGT.toFixed(2), 
      effectiveWBGT: effectiveWBGT.toFixed(2), 
      limit, status, cav, cycle 
    };
  }, [formData, riskLabels]);

  // MANEJADOR DE DESCARGA: Ahora solo llama al servicio
  const handleDownloadReport = () => {
    if (!evaluation) return;
    generateHeatReport(formData, evaluation);
  };

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  return (
    <div className="grid gap-4 lg:grid-cols-12 lg:gap-8">
      {/* COLUMNA IZQUIERDA: INPUTS (Mantenemos tu estructura de inputs completa) */}
      <div className="space-y-4 lg:col-span-7 lg:space-y-6">
        <div className="rounded-xl border border-border bg-surface p-4 sm:rounded-2xl sm:p-6">
          <div className="mb-4 flex items-center gap-2 border-b border-border pb-3 sm:mb-6 sm:gap-3 sm:pb-4">
            <ThermometerSun className="shrink-0 text-brand-primary" size={20} />
            <h3 className="font-display text-base font-bold text-brand-dark sm:text-xl">Datos de Medición</h3>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase text-gray-500 sm:text-xs">Temp. Bulbo Húmedo (Tnw) °C</label>
              <input name="tgb" type="number" value={formData.tgb} onChange={handleInput} placeholder="Ej: 24.5" className="w-full rounded-xl border border-border bg-white p-2.5 font-bold outline-none focus:ring-2 focus:ring-brand-accent sm:p-3" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase text-gray-500 sm:text-xs">Temp. de Globo (Tg) °C</label>
              <input name="tg" type="number" value={formData.tg} onChange={handleInput} placeholder="Ej: 35.0" className="w-full rounded-xl border border-border bg-white p-2.5 font-bold outline-none focus:ring-2 focus:ring-brand-accent sm:p-3" />
            </div>
          </div>

          <div className="mt-4 p-4 bg-white/50 rounded-xl border border-dashed border-gray-200">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" name="isOutdoor" checked={formData.isOutdoor} onChange={handleInput} className="w-5 h-5 accent-brand-primary" />
              <span className="text-sm font-bold text-brand-dark">Medición en exteriores con carga solar</span>
            </label>
            {formData.isOutdoor && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-4">
                <label className="text-xs font-bold uppercase text-gray-500">Temp. Aire / Bulbo Seco (Ta) °C</label>
                <input name="ta" type="number" value={formData.ta} onChange={handleInput} className="w-full mt-1 p-3 bg-white border border-border rounded-xl outline-none" />
              </motion.div>
            )}
          </div>
        </div>

        {/* Factores de Exposición */}
        <div className="rounded-xl border border-border bg-surface p-4 sm:rounded-2xl sm:p-6">
          <div className="mb-4 flex items-center gap-2 border-b border-border pb-3 sm:mb-6 sm:gap-3 sm:pb-4">
            <Activity className="shrink-0 text-brand-primary" size={20} />
            <h3 className="font-display text-base font-bold text-brand-dark sm:text-xl">Factores de Exposición</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase text-gray-500 sm:text-xs">Carga de Trabajo</label>
              <select name="workClass" value={formData.workClass} onChange={handleInput} className="w-full rounded-xl border border-border bg-white p-2.5 font-medium sm:p-3">
                <option value="class1">Clase 1: Ligera</option>
                <option value="class2">Clase 2: Moderada</option>
                <option value="class3">Clase 3: Pesada</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase text-gray-500 sm:text-xs">Tipo de Ropa</label>
              <select name="clothing" value={formData.clothing} onChange={handleInput} className="w-full rounded-xl border border-border bg-white p-2.5 font-medium sm:p-3">
                <option value="conventional">Ropa de trabajo (0°C)</option>
                <option value="coverall">Overol de tela (+0.5°C)</option>
                <option value="double_layer">Doble capa (+3°C)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* COLUMNA DERECHA: DASHBOARD DE RESULTADOS */}
      <div className="lg:col-span-5">
        <AnimatePresence mode="wait">
          {!evaluation ? (
            <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center sm:rounded-[32px] sm:p-12">
              <ThermometerSun size={36} className="mb-3 text-gray-300 sm:mb-4" />
              <p className="text-sm font-medium text-gray-400 sm:text-base">Ingresa los datos para <br /> generar el diagnóstico.</p>
            </div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 sm:space-y-6">
              
              {/* STATUS CARD */}
              <div className={`${evaluation.status.bg} rounded-2xl border border-white p-5 shadow-xl sm:rounded-[32px] sm:p-8`}>
                <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${evaluation.status.color}`}>WBGT Efectivo</span>
                <div className={`my-2 font-display text-4xl font-black sm:text-6xl ${evaluation.status.color}`}>
                  {evaluation.effectiveWBGT}°
                </div>
                <div className={`flex items-center gap-2 font-bold ${evaluation.status.color}`}>
                  {evaluation.status.icon === 'hazard' ? <ShieldAlert /> : <CheckCircle2 />}
                  {evaluation.status.label}
                </div>
              </div>

              {/* RÉGIMEN RECOMENDADO */}
              <div className="bg-white p-6 rounded-[32px] border border-border shadow-sm">
                <h4 className="flex items-center gap-2 text-brand-dark text-xs font-bold uppercase tracking-widest mb-6 text-center justify-center">
                   <Timer size={16} className="text-brand-primary" /> Régimen Recomendado
                </h4>
                <div className="flex items-center justify-between gap-4">
                  <div className="text-center flex-1">
                    <div className={`text-2xl font-black ${evaluation.cycle.color}`}>{evaluation.cycle.work}</div>
                    <div className="text-[10px] uppercase font-bold text-gray-400">Trabajo</div>
                  </div>
                  <ArrowRight className="text-gray-300" />
                  <div className="text-center flex-1">
                    <div className="text-2xl font-black text-gray-300">{evaluation.cycle.rest}</div>
                    <div className="text-[10px] uppercase font-bold text-gray-400">Descanso</div>
                  </div>
                </div>
              </div>

              {/* BOTÓN DE PDF (CONECTADO AL SERVICIO) */}
              <button 
                onClick={handleDownloadReport}
                className="w-full flex items-center justify-center gap-3 bg-brand-dark text-brand-accent py-5 rounded-[24px] font-bold hover:bg-black transition-all shadow-lg active:scale-95"
              >
                <FileDown size={20} />
                Descargar Reporte Técnico PDF
              </button>

              <div className="p-6 bg-brand-soft/30 rounded-[24px] border border-brand-soft">
                <p className="text-[11px] text-brand-primary leading-relaxed flex gap-3 text-center justify-center">
                  <Info size={24} className="shrink-0" />
                  <span>Este reporte técnico está alineado con la ISO 7243:2017.</span>
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default HeatStress;
/*<!--© 2026 Todos los derechos reservados Desarrollado por JEMAprod-Tecnologia-->*/